/**
 * Core: Canvas imports
 * -----------------------------------------------------------------------------
 * Import async components, global components, and Vuex stores for use in
 * CanvasApp Vue instance.
 *
 */
/* eslint-disable max-len, no-inline-comments, quote-props */
import { defineAsyncComponent } from 'vue'

/**
 * Get async components and imports.
 * - Must be manually updated with new async components (components/async/).
 * - Previously used a dynamic import statement which caused infinite build
 *   issue and generated unused files.
 * - Only parent components should be imported.
 * - Do not import components that are only ever used as a child component.
 * - Do not import async sub-components.
 * @returns {Object}
 */
export function getAsyncComponents() {
  return {
    // canvas-async-object-start
    'accordion-item': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.accordion-item' */'~async/accordion-item/accordion-item') }),
    'blog-header': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.blog-header' */'~async/blog-header/blog-header') }),
    'brand-values': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.brand-values' */'~async/brand-values/brand-values') }),
    'cart-drawer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.cart-drawer' */'~async/cart-drawer/cart-drawer') }),
    'collection-filter': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.collection-filter' */'~async/collection-filter/collection-filter') }),
    'collection-grid': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.collection-grid' */'~async/collection-grid/collection-grid') }),
    'collection-hero': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.collection-hero' */'~async/collection-hero/collection-hero') }),
    'contact-dealer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.contact-dealer' */'~async/contact-dealer/contact-dealer') }),
    'contact-us': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.contact-us' */'~async/contact-us/contact-us') }),
    'content-doorways': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.content-doorways' */'~async/content-doorways/content-doorways') }),
    'country-selector': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.country-selector' */'~async/country-selector/country-selector') }),
    'customers-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-account' */'~async/customers/account/customers-account') }),
    'customers-activate-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-activate-account' */'~async/customers/activate-account/customers-activate-account') }),
    'customers-addresses': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-addresses' */'~async/customers/addresses/customers-addresses') }),
    'customers-login': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-login' */'~async/customers/login/customers-login') }),
    'customers-order': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-order' */'~async/customers/order/customers-order') }),
    'customers-orders': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-orders' */'~async/customers/orders/customers-orders') }),
    'customers-register': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-register' */'~async/customers/register/customers-register') }),
    'customers-reset-password': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-reset-password' */'~async/customers/reset-password/customers-reset-password') }),
    'faq-accordion': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.faq-accordion' */'~async/faq-accordion/faq-accordion') }),
    'feature-highlights': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.feature-highlights' */'~async/feature-highlights/feature-highlights') }),
    'hero-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.hero-banner' */'~async/hero-banner/hero-banner') }),
    'main-404': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-404' */'~async/main-404/main-404') }),
    'main-article': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-article' */'~async/main-article/main-article') }),
    'main-blog': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-blog' */'~async/main-blog/main-blog') }),
    'main-cart': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-cart' */'~async/main-cart/main-cart') }),
    'main-collection': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-collection' */'~async/main-collection/main-collection') }),
    'main-list-collections': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-list-collections' */'~async/main-list-collections/main-list-collections') }),
    'main-page': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-page' */'~async/main-page/main-page') }),
    'main-product': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-product' */'~async/main-product/main-product') }),
    'main-search': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-search' */'~async/main-search/main-search') }),
    'product-badge': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-badge' */'~async/product-badge/product-badge') }),
    'product-card-sawmill': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-card-sawmill' */'~async/product-card-sawmill/product-card-sawmill') }),
    'product-compare-chart': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-compare-chart' */'~async/product-compare-chart/product-compare-chart') }),
    'product-compare-chart-card': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-compare-chart-card' */'~async/product-compare-chart-card/product-compare-chart-card') }),
    'product-comparison': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-comparison' */'~async/product-comparison/product-comparison') }),
    'product-details-drawer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-details-drawer' */'~async/product-details-drawer/product-details-drawer') }),
    'product-fifty-fifty': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-fifty-fifty' */'~async/product-fifty-fifty/product-fifty-fifty') }),
    'product-rating': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-rating' */'~async/product-rating/product-rating') }),
    'product-reviews': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-reviews' */'~async/product-reviews/product-reviews') }),
    'promo-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.promo-banner' */'~async/promo-banner/promo-banner') }),
    'recommended-products': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.recommended-products' */'~async/recommended-products/recommended-products') }),
    'site-footer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.site-footer' */'~async/site-footer-group/site-footer/site-footer') }),
    'technical-specs': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.technical-specs' */'~async/technical-specs/technical-specs') }),
    'transition-height': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.transition-height' */'~async/transition-height/transition-height') }),
    // 'cnvs-featured-products': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.cnvs-featured-products' */'~async/cnvs-featured-products/cnvs-featured-products') }),
    // canvas-async-object-end
  }
}

/**
 * Import global components here.
 */
// canvas-global-import-start
import Accessibility from '~global/core/accessibility'
import ArtDirection from '~global/images/art-direction'
import Btn from '~global/btn/btn'
import FeatureHighlightCard from '~global/feature-highlight-card/feature-highlight-card'
import Index from '~global/core/index'
import CountrySelectorBtn from '~global/country-selector-btn/country-selector-btn'
import Overlay from '~global/overlay/overlay'
import ProductPrices from '~global/product-prices/product-prices'
import ProductRange from '~global/product-range/product-range'
import ProductStat from '~global/product-stat/product-stat'
import ResponsiveImage from '~global/images/responsive-image'
import ResponsiveVideo from '~global/responsive-video/responsive-video'
import SiteHeader from '~global/site-header-group/site-header/site-header'
import SiteHeaderGroup from '~global/site-header-group/site-header-group'
import SplideArrows from '~global/splide-arrows/splide-arrows'
// canvas-global-import-end

/**
 * Get global components and imports.
 * - Must be manually updated with new global components (components/global).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 * @returns {Object}
 */
export function getGlobalComponents() {
  return {
    // canvas-global-object-start
    'accessibility': Accessibility,
    'art-direction': ArtDirection,
    'btn': Btn,
    'country-selector-btn': CountrySelectorBtn,
    'feature-highlight-card': FeatureHighlightCard,
    'index': Index,
    'overlay': Overlay,
    'product-prices': ProductPrices,
    'product-range': ProductRange,
    'product-stat': ProductStat,
    'responsive-image': ResponsiveImage,
    'responsive-video': ResponsiveVideo,
    'site-header': SiteHeader,
    'site-header-group': SiteHeaderGroup,
    'splide-arrows': SplideArrows,
    // canvas-global-object-end
  }
}

/**
 * Import Vuex stores here.
 * - Import with Store suffix to avoid conflicts with global component imports.
 */
// canvas-stores-import-start
import AccessibilityStore from '~stores/accessibility'
import CartStore from '~stores/cart'
import ComponentsStore from '~stores/components'
import IndexStore from '~stores/index'
import OverlaysStore from '~stores/overlays'
import SiteHeaderStore from '~stores/site-header'
// canvas-stores-import-end

/**
 * Get Vuex stores and imports.
 * - Must be manually updated with new Vuex stores (stores/).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 */
export function getVuexStores() {
  return {
    // canvas-stores-object-start
    'accessibility': AccessibilityStore,
    'cart': CartStore,
    'components': ComponentsStore,
    'index': IndexStore,
    'overlays': OverlaysStore,
    'site-header': SiteHeaderStore,
    // canvas-stores-object-end
  }
}
